import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE } from "../config";
import AvatarPicker from "./AvatarPicker";
import { openGameHelp } from "../utils/openGameHelp";
import { restoreTableSession } from "../api";

function normalizeTableCode(code) {
  return (code || "").trim().toLowerCase().replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
}

function getParam(name) {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  return params.get(name) || "";
}

function getStoredPlayer() {
  try {
    return JSON.parse(localStorage.getItem("player")) || null;
  } catch (e) {
    return null;
  }
}

function getStoredTableID() {
  try {
    return localStorage.getItem("tableID") || "";
  } catch (e) {
    return "";
  }
}

function clearSavedSession() {
  try {
    localStorage.removeItem("player");
    localStorage.removeItem("tableID");
    window.history.replaceState({}, document.title, window.location.pathname);
  } catch (e) {
    // ignore
  }
}

export default function Lobby({ onJoin }) {
  const [myName, setMyName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("/avatars/avatar1.png");
  const [botCount, setBotCount] = useState(2);
  const [creditValue, setCreditValue] = useState(0.25);
  const [tableCode, setTableCode] = useState("");
  const [inviteError, setInviteError] = useState("");
  const [loading, setLoading] = useState(false);
  const [savedSession, setSavedSession] = useState(null);
  const [inviteBanner, setInviteBanner] = useState("");

  useEffect(() => {
    const tableParam = getParam("table");
    const storedPlayer = getStoredPlayer();
    const storedTableID = getStoredTableID();

    if (tableParam) {
      const code = normalizeTableCode(tableParam);
      setTableCode(code);
      if (getParam("invite") === "1") {
        setInviteBanner(`Invite link for table ${code}. You can change the code below to join a different table.`);
      }
    }

    if (storedPlayer?.playerID && storedTableID) {
      setSavedSession({
        tableID: normalizeTableCode(storedTableID),
        playerID: storedPlayer.playerID,
        name: storedPlayer.name || "",
        avatarUrl: storedPlayer.avatarUrl || "/avatars/avatar1.png",
      });
      if (!myName && storedPlayer.name) setMyName(storedPlayer.name);
      if (storedPlayer.avatarUrl) setAvatarUrl(storedPlayer.avatarUrl);
    }
  }, []);

  async function createTable() {
    setLoading(true);
    setInviteError("");
    try {
      const { data: { tableID } } = await axios.post(
        `${API_BASE}/table`,
        { botCount, creditValue }
      );
      const { data: { playerID } } = await axios.post(
        `${API_BASE}/table/${tableID}/join`,
        { name: myName || "Player", avatarUrl }
      );
      onJoin({ tableID, playerID, name: myName, avatarUrl, isSpectator: false });
    } catch (e) {
      setInviteError(e.response?.data?.error || e.message || "Table create failed.");
    } finally {
      setLoading(false);
    }
  }

  async function joinTableManual(joinTableID) {
    setLoading(true);
    setInviteError("");
    try {
      const code = normalizeTableCode(joinTableID || tableCode);
      const { data: { playerID } } = await axios.post(
        `${API_BASE}/table/${code}/join`,
        { name: myName || "Player", avatarUrl }
      );
      onJoin({
        tableID: code,
        playerID,
        name: myName,
        avatarUrl,
        isSpectator: false,
      });
    } catch (e) {
      setInviteError(e.response?.data?.error || "Table not found. Use the same site URL as the host (https://gutz.sbfchoice.cc) and verify the 6-character table code.");
    } finally {
      setLoading(false);
    }
  }

  async function joinTableAsSpectator(joinTableID) {
    setLoading(true);
    setInviteError("");
    try {
      const code = normalizeTableCode(joinTableID || tableCode);
      const { data: { playerID } } = await axios.post(
        `${API_BASE}/table/${code}/join`,
        { name: myName || "Spectator", avatarUrl, isSpectator: true }
      );
      onJoin({
        tableID: code,
        playerID,
        name: myName,
        avatarUrl,
        isSpectator: true,
      });
    } catch (e) {
      setInviteError("Table not found or join failed.");
    } finally {
      setLoading(false);
    }
  }

  async function rejoinSavedSession() {
    if (!savedSession) return;
    setLoading(true);
    setInviteError("");
    try {
      const displayName = myName.trim() || savedSession.name || "Player";
      await restoreTableSession(
        savedSession.tableID,
        savedSession.playerID,
        displayName,
        avatarUrl
      );
      onJoin({
        tableID: savedSession.tableID,
        playerID: savedSession.playerID,
        name: displayName,
        avatarUrl,
        isSpectator: false,
        rejoin: true,
      });
    } catch (e) {
      setInviteError(
        e.response?.data?.error ||
          "Could not rejoin your last table. It may have ended — join or create a different table below."
      );
    } finally {
      setLoading(false);
    }
  }

  function dismissSavedSession() {
    clearSavedSession();
    setSavedSession(null);
    setInviteError("");
  }

  return (
    <div className="guts-lobby-card">
      <h1
        style={{
          textAlign: "center",
          marginBottom: 5,
          fontWeight: 700,
          fontSize: "2.25rem",
          color: "#624906",
          letterSpacing: "-0.5px",
          fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
        }}
      >
        Guts Table
      </h1>
      <div
        style={{
          textAlign: "center",
          color: "#947b3d",
          fontSize: "1.10rem",
          marginBottom: ".9rem",
          fontWeight: 500,
          letterSpacing: 0,
        }}
      >
        Join or create a new game
      </div>

      {inviteBanner && (
        <div
          style={{
            background: "#fff8dc",
            border: "1.5px solid #e0cb95",
            borderRadius: 9,
            padding: "10px 14px",
            marginBottom: "1rem",
            color: "#624906",
            fontSize: "0.95rem",
            textAlign: "center",
          }}
        >
          {inviteBanner}
        </div>
      )}

      {savedSession && (
        <div
          style={{
            background: "#f0f7ff",
            border: "1.5px solid #9ec5f0",
            borderRadius: 9,
            padding: "14px 16px",
            marginBottom: "1.2rem",
          }}
        >
          <div style={{ fontWeight: 700, color: "#2c5282", marginBottom: 6 }}>
            Resume your last table
          </div>
          <div style={{ color: "#4a5568", fontSize: "0.95rem", marginBottom: 12 }}>
            Table <strong>{savedSession.tableID}</strong> — your stats and payment history are saved while you&apos;re away.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={rejoinSavedSession}
              disabled={loading}
              style={{
                padding: "10px 18px",
                borderRadius: 9,
                background: "#3182ce",
                color: "#fff",
                fontWeight: 700,
                border: "none",
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              {loading ? "Rejoining..." : "Rejoin Last Table"}
            </button>
            <button
              type="button"
              onClick={dismissSavedSession}
              disabled={loading}
              style={{
                padding: "10px 18px",
                borderRadius: 9,
                background: "#f8f4ea",
                color: "#876f38",
                fontWeight: 600,
                border: "1.5px solid #e0cb95",
                cursor: "pointer",
              }}
            >
              Forget saved session
            </button>
          </div>
        </div>
      )}

      <div style={{ width: "100%", marginBottom: "1.19rem" }}>
        <label
          style={{
            fontSize: "1rem",
            display: "block",
            color: "#876f38",
            marginBottom: 6,
            fontWeight: 600,
          }}
        >
          Display Name
        </label>
        <input
          value={myName}
          onChange={(e) => {
            const sentenceCase = e.target.value.charAt(0).toUpperCase() + e.target.value.slice(1).toLowerCase();
            setMyName(sentenceCase);
          }}
          maxLength={18}
          style={{
            fontSize: "1.13rem",
            width: "100%",
            padding: "11px 13px",
            border: "1.7px solid #e0cb95",
            borderRadius: 9,
            outline: 0,
            background: "#f9f4e5",
            color: "#4d3912",
            fontWeight: 520,
            marginBottom: ".5rem",
            boxSizing: "border-box",
          }}
          autoFocus
          placeholder="Your name"
        />
      </div>
      <div
        style={{
          width: "100%",
          marginBottom: 18,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <label
          style={{
            fontWeight: 600,
            fontSize: "1rem",
            color: "#876f38",
            marginBottom: 4,
            width: "100%",
          }}
        >
          Pick Your Avatar
        </label>
        <AvatarPicker value={avatarUrl} onChange={setAvatarUrl} />
      </div>
      <div style={{ width: "100%", marginBottom: "1.38rem" }}>
        <label
          style={{
            fontWeight: 600,
            fontSize: "1rem",
            color: "#876f38",
            marginBottom: 6,
            display: "block",
          }}
        >
          Bots{" "}
          <span style={{ fontWeight: 400, color: "#b6ab87", fontSize: "0.95em" }}>
            (2-7 total seats)
          </span>
        </label>
        <select
          value={botCount}
          onChange={(e) => setBotCount(Number(e.target.value))}
          style={{
            fontSize: "1.08rem",
            width: "100%",
            padding: "10px 11px",
            border: "1.5px solid #e5d29b",
            borderRadius: 9,
            background: "#f9f6eb",
            color: "#624906",
            fontWeight: 500,
            boxSizing: "border-box",
            marginBottom: 0,
            outline: 0,
          }}
        >
          {Array.from({ length: 7 }, (_, i) => i).map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </div>
      <div style={{ width: "100%", marginBottom: "1.38rem" }}>
        <label
          style={{
            fontWeight: 600,
            fontSize: "1rem",
            color: "#876f38",
            marginBottom: 6,
            display: "block",
          }}
        >
          Credit Value{" "}
          <span style={{ fontWeight: 400, color: "#b6ab87", fontSize: "0.95em" }}>
            (per chip)
          </span>
        </label>
        <select
          value={creditValue}
          onChange={(e) => setCreditValue(Number(e.target.value))}
          style={{
            fontSize: "1.08rem",
            width: "100%",
            padding: "10px 11px",
            border: "1.5px solid #e5d29b",
            borderRadius: 9,
            background: "#f9f6eb",
            color: "#624906",
            fontWeight: 500,
            boxSizing: "border-box",
            marginBottom: 0,
            outline: 0,
          }}
        >
          <option value={0.25}>$0.25</option>
          <option value={0.5}>$0.50</option>
          <option value={0.75}>$0.75</option>
          <option value={1.0}>$1.00</option>
        </select>
      </div>
      <div className="guts-lobby-actions">
        <button
          onClick={createTable}
          disabled={!myName.trim() || loading}
          style={{
            fontWeight: 700,
            fontSize: "1.15rem",
            padding: "10px 34px",
            borderRadius: 11,
            background: loading ? "#ccf" : "#fff36d",
            backgroundImage: loading ? "none" : "linear-gradient(94deg,#ffea76 0%,#ffe596 100%)",
            color: "#6f5a19",
            border: "none",
            letterSpacing: "0.01em",
            cursor: loading ? "not-allowed" : "pointer",
            boxShadow: "0 1.5px 9px #d5ae5131",
            transition: "background .13s",
          }}
        >
          {loading ? "Creating Table..." : "Create Table"}
        </button>
      </div>
      <div style={{ borderTop: "1.5px solid #dde", margin: "30px 0", paddingTop: 18, width: "100%" }}>
        <div style={{ fontWeight: "600", fontSize: "1rem", color: "#876f38", marginBottom: "12px" }}>
          Join Table:
        </div>
        <div className="guts-lobby-join-row">
          <input
            className="guts-lobby-join-code-input"
            style={{
              flex: 1,
              fontSize: "1.08rem",
              padding: "10px 11px",
              border: "1.5px solid #e5d29b",
              borderRadius: 9,
              background: "#f9f6eb",
              color: "#624906",
              fontWeight: 600,
              outline: 0,
            }}
            value={tableCode}
            onChange={(e) => {
              setInviteError("");
              setTableCode(normalizeTableCode(e.target.value));
            }}
            maxLength={7}
            placeholder="e.g. a3f29c"
            inputMode="text"
            autoComplete="off"
            spellCheck={false}
          />
          <div className="guts-lobby-join-actions">
            <button
              onClick={() => joinTableManual()}
              disabled={!tableCode || !myName.trim() || loading}
              style={{
                padding: "10px 16px",
                borderRadius: 9,
                background: "#68a2e7",
                color: "#fff",
                fontWeight: "bold",
                border: "none",
                cursor: "pointer",
              }}
            >
              Join
            </button>
            <button
              style={{
                padding: "10px 16px",
                fontSize: "1em",
                border: "1px solid #ccc",
                borderRadius: 9,
                background: "#eee",
                color: "#444",
                cursor: "pointer",
              }}
              onClick={() => joinTableAsSpectator()}
              disabled={!tableCode || !myName.trim() || loading}
            >
              Spectate
            </button>
          </div>
        </div>
        {inviteError && (
          <div
            role="alert"
            style={{
              color: "#d13c3c",
              fontWeight: 600,
              fontSize: "1.09em",
              marginTop: 8,
              textAlign: "center",
            }}
          >
            {inviteError}
          </div>
        )}
        <button
          type="button"
          onClick={openGameHelp}
          style={{
            width: "100%",
            marginTop: "12px",
            padding: "10px 16px",
            borderRadius: 9,
            border: "1.5px solid #e0cb95",
            background: "#f8f4ea",
            color: "#876f38",
            fontWeight: 600,
            fontSize: "0.95rem",
            cursor: "pointer",
          }}
        >
          How to Play & Features Guide
        </button>
      </div>
    </div>
  );
}
